import type { Settings, Threshold } from '@painpal/contracts';
import { useEffect, useRef, useState } from 'react';
import { useUpdateSettings } from '../../api/mutations';
import { useSettings } from '../../api/queries';
import { ErrorPanel, LoadingBlock } from '../../components/StateViews';
import { ToggleTabs } from '../../components/ToggleTabs';
import { errorMessage } from '../../lib/api';
import { formatDayMonthYear } from '../../lib/format';
import { ThresholdHistory, type Editing } from './ThresholdHistory';
import { MaxHrSuggestion } from './ThresholdSuggestions';
import { currentThreshold, THRESHOLD_COPY, type ThresholdCopy } from './thresholdModel';
import styles from './settings.module.css';

type HrModel = Settings['hrZoneModel'];

const OPTIONS: readonly { value: HrModel; label: string }[] = [
  { value: 'max_hr', label: 'MAX HR · 5 ZONES' },
  { value: 'lthr', label: 'LTHR · 7 ZONES' },
];

interface CurrentProps { copy: ThresholdCopy; items: Threshold[]; today: string; onAdd: () => void }

function CurrentValue({ copy, items, today, onAdd }: CurrentProps) {
  const current = currentThreshold(items, today);
  return (
    <div className={styles.hrCurrent}>
      {current !== undefined && (
        <p className={styles.hrValueLine}>
          <span className={styles.hrValue}>{`${current.value} ${copy.unit}`}</span>
          <span className={styles.hrSince}>{`${copy.short} · SINCE ${formatDayMonthYear(current.effectiveFrom)}`}</span>
        </p>
      )}
      <button type="button" className="btn btn-outline" onClick={onAdd}>{`+ ADD ${copy.short}`}</button>
    </div>
  );
}

function HrHistory({ model, today }: { model: HrModel; today: string }) {
  const copy = THRESHOLD_COPY[model];
  const [editing, setEditing] = useState<Editing>(null);
  return (
    <div className={styles.hrHistory}>
      <ThresholdHistory
        copy={copy} today={today} editing={editing} setEditing={setEditing}
        summary={(items) => (
          <>
            {items.length > 0 && <CurrentValue copy={copy} items={items} today={today} onAdd={() => setEditing({ mode: 'new' })} />}
            <MaxHrSuggestion />
          </>
        )}
      />
    </div>
  );
}

interface HrZonesCardProps { today: string; focusHeading?: boolean; onFocused?: () => void }

export function HrZonesCard({ today, focusHeading = false, onFocused }: HrZonesCardProps) {
  const settings = useSettings();
  const update = useUpdateSettings();
  const heading = useRef<HTMLHeadingElement>(null);
  useEffect(() => {
    if (!focusHeading) return;
    heading.current?.focus();
    onFocused?.();
  }, [focusHeading, onFocused]);
  return (
    <section className={styles.card} aria-labelledby="hr-zones-title">
      <h2 id="hr-zones-title" ref={heading} tabIndex={-1} className={styles.cardTitle}>HR ZONES</h2>
      {settings.isPending && <LoadingBlock label="LOADING HR ZONES" height={80} />}
      {settings.isError && <ErrorPanel error={settings.error} onRetry={() => void settings.refetch()} />}
      {settings.isSuccess && (
        <>
          <ToggleTabs
            label="HR zone model" options={OPTIONS} value={settings.data.hrZoneModel} block disabled={update.isPending}
            onChange={(hrZoneModel) => update.mutate({ hrZoneModel })}
          />
          <HrHistory key={settings.data.hrZoneModel} model={settings.data.hrZoneModel} today={today} />
          <p className={styles.note}>Both histories are kept; this picks which HR zones every ride shows.</p>
        </>
      )}
      {update.isError && <p role="alert" className={`alert-hot ${styles.formError}`}>{errorMessage(update.error)}</p>}
    </section>
  );
}
